// LagerPro als App auf dem Rechner — zwei Wege:
//
//   Direkt installieren   aus dem Browser heraus (Chrome/Edge „Installieren",
//                         Safari „Zum Dock hinzufügen"). Sekunden, keine
//                         Warnung, aktualisiert sich selbst.
//   Herunterladen         die Desktop-App aus desktop/: .exe für Windows, .dmg
//                         für Mac mit Apple-Chip und für Mac mit Intel-Chip.
//
// Beide merken sich die Anmeldung 90 Tage. Welche Dateien es gibt, steht in
// /downloads/latest.json neben den Dateien — die Seite kennt keine Version
// fest, ein neuer Build braucht keinen neuen Frontend-Stand.
//
// Steht auf der Startseite (kompakt), als Menüpunkt „App" und unter /download.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { useSprache, type Schluessel } from '../../../gemeinsam/lib/i18n';
import { Hinweis, Knopf } from '../../../gemeinsam/komponenten/Basis';
import Icon from '../../../gemeinsam/komponenten/Icon';
import {
  bruecke, installieren, installStand, installStandBeobachten, istDesktopApp, istInstallierteWebApp, systemName,
} from '../../anmeldung/lib/desktop';

type Datei = { datei: string; groesse: number; sha256?: string };
type Ausgabe = { version: string; datum: string; dateien: Partial<Record<'windows' | 'mac-arm64' | 'mac-x64', Datei>> };
type Ziel = 'windows' | 'mac-arm64' | 'mac-x64';

const ZIELE: Array<{ id: Ziel; titel: Schluessel; unter: Schluessel }> = [
  { id: 'windows', titel: 'desktop.windows', unter: 'desktop.windowsUnter' },
  { id: 'mac-arm64', titel: 'desktop.macApple', unter: 'desktop.macAppleUnter' },
  { id: 'mac-x64', titel: 'desktop.macIntel', unter: 'desktop.macIntelUnter' },
];

const mb = (b: number) => `${(b / 1024 / 1024).toLocaleString(undefined, { maximumFractionDigits: 0 })} MB`;

/** Welcher Mac? Chrome/Edge sagen es direkt; sonst verrät es meist der Grafikchip. */
async function macChip(): Promise<'mac-arm64' | 'mac-x64' | null> {
  const uad = (navigator as Navigator & { userAgentData?: { getHighEntropyValues: (h: string[]) => Promise<{ architecture?: string }> } }).userAgentData;
  if (uad?.getHighEntropyValues) {
    try {
      const h = await uad.getHighEntropyValues(['architecture']);
      if (h.architecture === 'arm') return 'mac-arm64';
      if (h.architecture === 'x86') return 'mac-x64';
    } catch { /* weiter mit der Grafik */ }
  }
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    const ext = gl?.getExtension('WEBGL_debug_renderer_info');
    const r = ext && gl ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : '';
    if (/Apple M\d/i.test(r)) return 'mac-arm64';
    if (/Intel|AMD|Radeon/i.test(r)) return 'mac-x64';
  } catch { /* unbekannt */ }
  return null; // Safari verrät es nicht — dann beide gleichrangig zeigen
}

function browser(): 'safari' | 'firefox' | 'chromium' | 'andere' {
  const ua = navigator.userAgent;
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Edg\/|Chrome\//.test(ua)) return 'chromium';
  if (/Safari\//.test(ua)) return 'safari';
  return 'andere';
}

export default function AppHerunterladen({ kompakt = false }: { kompakt?: boolean }) {
  const { t } = useSprache();
  const stand = useSyncExternalStore(installStandBeobachten, () => JSON.stringify(installStand()));
  const { moeglich, installiert } = JSON.parse(stand) as ReturnType<typeof installStand>;
  const [ausgabe, setAusgabe] = useState<Ausgabe | null | 'fehlt'>(null);
  const [empfohlen, setEmpfohlen] = useState<Ziel | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  useEffect(() => {
    // Eigene Abfrage statt api(): die Liste liegt statisch neben den Dateien.
    fetch('/downloads/latest.json', { cache: 'no-cache' })
      .then(r => (r.ok && (r.headers.get('content-type') || '').includes('json') ? r.json() : Promise.reject()))
      .then((a: Ausgabe) => setAusgabe(a))
      .catch(() => setAusgabe('fehlt'));
    const sys = systemName();
    if (sys === 'windows') setEmpfohlen('windows');
    else if (sys === 'mac') void macChip().then(setEmpfohlen);
  }, []);

  async function direkt() {
    const r = await installieren();
    if (r === 'angenommen') setMeldung(t('desktop.installiert'));
  }

  const b = bruecke();
  const br = browser();
  const dateien = ausgabe && ausgabe !== 'fehlt' ? ausgabe.dateien : {};
  const reihenfolge = [...ZIELE].sort((x, y) => Number(y.id === empfohlen) - Number(x.id === empfohlen));

  return (
    <div className="space-y-5">
      {!kompakt && (
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('desktop.titel')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600 dark:text-slate-400">{t('desktop.text')}</p>
        </div>
      )}

      {istDesktopApp() && b && (
        <Hinweis art="erfolg" titel={t('desktop.inApp')}>
          {t('desktop.inAppText').replace('{version}', b.version)}
        </Hinweis>
      )}
      {istInstallierteWebApp() && <Hinweis art="erfolg">{t('desktop.alsWebApp')}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      {/* --- Weg 1: direkt aus dem Browser -------------------------------- */}
      {!istDesktopApp() && !installiert && (
        <div className="rounded-xl border border-marke-200 bg-marke-50/60 p-5 dark:border-marke-900 dark:bg-marke-950/30">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
                {t('desktop.direktTitel')}
                <span className="rounded-full bg-marke-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">{t('desktop.schnell')}</span>
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                {moeglich ? t('desktop.direktText')
                  : br === 'safari' && systemName() === 'mac' ? t('desktop.safari')
                  : br === 'firefox' ? t('desktop.firefox')
                  : br === 'chromium' ? t('desktop.chromeLeiste')
                  : t('desktop.andererBrowser')}
              </p>
            </div>
            {moeglich && (
              <Knopf variante="primaer" onClick={direkt}><Icon name="herunterladen" groesse={18} />{t('desktop.jetztInstallieren')}</Knopf>
            )}
          </div>
        </div>
      )}

      {/* --- Weg 2: Download ---------------------------------------------- */}
      <div>
        <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
          {t('desktop.downloadTitel')}
          {ausgabe && ausgabe !== 'fehlt' && <span className="ms-2 font-normal text-slate-500 dark:text-slate-400">· {t('desktop.version').replace('{version}', ausgabe.version)}</span>}
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          {reihenfolge.map(z => {
            const d = dateien[z.id];
            const hervor = z.id === empfohlen;
            return (
              <div key={z.id} className={`flex flex-col rounded-xl border p-4 ${hervor
                ? 'border-marke-400 ring-1 ring-marke-400 dark:border-marke-600 dark:ring-marke-600'
                : 'border-slate-200 dark:border-slate-800'} bg-white dark:bg-slate-900`}>
                <p className="font-semibold text-slate-900 dark:text-slate-100">{t(z.titel)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t(z.unter)}</p>
                {hervor && <p className="mt-1 text-xs font-medium text-marke-700 dark:text-marke-300">{t('desktop.passtZuIhnen')}</p>}
                <div className="mt-auto pt-3">
                  {d ? (
                    <a href={`/downloads/${encodeURIComponent(d.datei)}`} download
                       className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${hervor
                         ? 'bg-marke-600 text-white hover:bg-marke-700'
                         : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'}`}>
                      <Icon name="herunterladen" groesse={16} />{t('desktop.laden')} · {mb(d.groesse)}
                    </a>
                  ) : (
                    <p className="rounded-lg bg-slate-100 px-3 py-2 text-center text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                      {ausgabe === null ? '…' : t('desktop.bald')}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!kompakt && (
        <details className="rounded-xl border border-slate-200 p-4 text-sm text-slate-700 dark:border-slate-800 dark:text-slate-300">
          <summary className="cursor-pointer font-medium text-slate-900 dark:text-slate-100">{t('desktop.warnungTitel')}</summary>
          <div className="mt-3 space-y-3">
            <p>{t('desktop.warnungText')}</p>
            <p><strong>Windows:</strong> {t('desktop.warnungWindows')}</p>
            <p><strong>Mac:</strong> {t('desktop.warnungMac')}</p>
          </div>
        </details>
      )}

      <p className="text-xs text-slate-500 dark:text-slate-400">{t('desktop.anmeldung')}</p>
    </div>
  );
}
